<template>
  <template v-if="cartModals">
    <Product
      v-fade-in
      v-for="submittableCartItem in submittableCartItems"
      :key="submittableCartItem.frontend_key"
      :submittable-cart-item="submittableCartItem"
      :product="product"
      :category-product-store-pair="categoryProductStorePair"
      :cart-quantity="cartQuantity"
      :in-cart="inCart"
      :in-upsell="inUpsell"
      :unavailable="unavailable"
    />
  </template>
  <template v-else>
    <ProductLegacy
      v-fade-in
      v-for="submittableCartItem in submittableCartItems"
      :key="submittableCartItem.frontend_key"
      :submittable-cart-item="submittableCartItem"
      :product="product"
      :category-product-store-pair="categoryProductStorePair"
      :in-cart="inCart"
      :in-upsell="inUpsell"
      @add-to-cart="bankItem"
    />
  </template>
</template>

<script>
import { ulid } from 'ulid'
import { Product } from '@/components'
import ProductLegacy from '@/components/Product/ProductLegacy.vue'
import {
  blankSubmittableCartItem as mintBlankSubmittableCartItem,
  cartLineSignature
} from '@/utils/submittableCartItem'

export default {
  name: 'CategoryProductStorePairContainer',

  components: {
    Product,
    ProductLegacy
  },

  props: ['categoryProductStorePair', 'inCart', 'inUpsell', 'unavailable'],

  data() {
    return {
      bankedKeys: [],
      freshBlankKey: null
    }
  },

  computed: {
    product() {
      return this.$store.getters.product(this.productRelationshipHash) //this.$store.getters.categories
    },
    productRelationshipHash() {
      return this.categoryProductStorePair?.relationships?.product?.data
    },
    submittableCartItemsForProduct() {
      return this.$store.getters.submittableCartItems(this.categoryProductStorePair.attributes.id).filter((x) => {
        if (this.$store.state.splitDay) return this.$store.state.splitDay === x.fulfillment_group_date
        else return true
      })
    },
    cartQuantity() {
      return (this.submittableCartItemsForProduct || []).reduce((total, item) => total + (item?.quantity || 0), 0)
    },
    blankSubmittableCartItem() {
      const blank = mintBlankSubmittableCartItem({
        categoryProductStorePairId: this.categoryProductStorePair?.id,
        fulfillmentGroupDate: this.$store.state.splitDay,
        isUpsell: this.inUpsell
      })
      return this.freshBlankKey ? { ...blank, frontend_key: this.freshBlankKey } : blank
    },
    cartModals() {
      return this.$store.getters.hasCartModals
    },
    drawerEnabled() {
      return this.$store.getters.hasCartDrawer
    },

    // Without the modals a card customizes its own line in place, so every line needs one and an
    // uncarted product needs a blank to start from. This is production's rule and only runs with
    // the capability off.
    showEmptyProductCard() {
      return this.product && (!this.submittableCartItemsForProduct || this.submittableCartItemsForProduct.length == 0)
    },

    // Only a card that *is* a cart line fans out one per line - the cart view, and the unavailable
    // list, whose pairs are off the menu and whose only control is Remove. On the menu a card
    // stands for the product, so it stays a single card however many lines the cart holds; tapping
    // it opens a blank modal, which is how a second variation gets ordered.
    submittableCartItems() {
      if (!this.cartModals) {
        if (this.drawerEnabled && !this.inCart) {
          // Cart drawer mode: one card per product — the customization being
          // configured. Lines banked via "Add to cart" live in the drawer; the
          // card resets to a fresh blank item.
          if (!this.product) return []
          const activeItems = this.submittableCartItemsForProduct.filter(
            (x) => !this.bankedKeys.includes(x.frontend_key)
          )
          const activeItem = activeItems[activeItems.length - 1]
          return [activeItem || this.blankSubmittableCartItem]
        }
        if (!this.inCart && this.showEmptyProductCard) return [this.blankSubmittableCartItem]
        if (this.inCart && this.showEmptyProductCard) return []
        return this.submittableCartItemsForProduct
      }
      if (this.inCart || this.unavailable) return this.submittableCartItemsForProduct
      if (!this.product) return []
      return [this.blankSubmittableCartItem]
    }
  },

  mounted() {},

  methods: {
    bankItem(frontendKey) {
      if (!frontendKey || this.bankedKeys.includes(frontendKey)) return
      // Same product + identical configuration as an existing line → merge
      // quantities into that line instead of stacking a duplicate.
      const item = this.submittableCartItemsForProduct.find((x) => x.frontend_key === frontendKey)
      const twins = item
        ? this.submittableCartItemsForProduct.filter(
            (other) => other.frontend_key !== frontendKey && cartLineSignature(other) === cartLineSignature(item)
          )
        : []
      if (twins.length) {
        const [keep, ...rest] = twins
        const total = twins.reduce((acc, t) => acc + (t.quantity || 0), item.quantity || 0)
        this.$store.dispatch('updateSubmittableCart', { submittableCartItem: { ...keep, quantity: total } })
        rest.forEach((t) =>
          this.$store.dispatch('updateSubmittableCart', { submittableCartItem: { ...t, quantity: 0 } })
        )
        this.$store.dispatch('updateSubmittableCart', { submittableCartItem: { ...item, quantity: 0 } })
      } else {
        this.bankedKeys.push(frontendKey)
      }
      this.freshBlankKey = ulid() + (this.inUpsell ? '-upsell' : '')
    }
  }
}
</script>
